import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useMemberTasks } from '@/hooks/useMemberTasks';
import { TeamMemberSelector } from '@/components/features/team-members';
import { MemberTimeline } from '@/components/features/timeline';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { supabase } from '@/lib/supabase';

/**
 * TeamMemberTimelinePage - Page component for viewing a team member's timeline
 *
 * Satisfies AC:
 * - AC9: Team Member Level Timeline is accessible from a team member list or selection
 * - AC10: Timeline provides clear visual feedback for all interactions
 * - AC11: Timeline is responsive and works on desktop and tablet
 * - AC12: Team member header displays: Member avatar/initials, name, role badge, total task count
 * - AC13: Member selector dropdown shows all team members with avatars, names, roles, and current task counts
 */
export const TeamMemberTimelinePage = () => {
    const navigate = useNavigate();
    const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

    // Fetch team members
    const {
        data: teamMembers = [],
        isLoading: membersLoading,
        error: membersError,
    } = useTeamMembers();

    // Fetch tasks for selected member
    const {
        data: memberTasks = [],
        isLoading: tasksLoading,
        error: tasksError,
    } = useMemberTasks(selectedMemberId);

    // Fetch task counts for all members upfront so the selector shows real workload (AC13)
    const { data: taskCounts = {} } = useQuery({
        queryKey: ['member-task-counts'],
        queryFn: async (): Promise<Record<number, number>> => {
            const { data, error } = await supabase
                .from('tasks')
                .select('assigned_member_id')
                .not('assigned_member_id', 'is', null);
            if (error) throw new Error(`Failed to fetch member task counts: ${error.message}`);
            const counts: Record<number, number> = {};
            for (const row of data ?? []) {
                if (row.assigned_member_id != null) {
                    counts[row.assigned_member_id] = (counts[row.assigned_member_id] ?? 0) + 1;
                }
            }
            return counts;
        },
        staleTime: 5 * 60 * 1000,
    });

    const selectedMember = teamMembers.find((m) => m.id === selectedMemberId);

    const handleBackToDashboard = () => {
        navigate('/');
    };

    if (membersLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="lg" />
                    <p className="mt-4 text-gray-600">Loading team members...</p>
                </div>
            </div>
        );
    }

    if (membersError) {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-7xl mx-auto">
                    <Alert variant="error">
                        <strong>Error loading team members:</strong>
                        <br />
                        {membersError instanceof Error ? membersError.message : 'Unknown error occurred'}
                    </Alert>
                    <Button onClick={handleBackToDashboard} className="mt-4">
                        Back to Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header (AC10: Clear visual feedback) */}
            <header className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            {/* Breadcrumb navigation */}
                            <nav aria-label="Breadcrumb" className="mb-1">
                                <ol className="flex items-center gap-1 text-sm text-gray-500">
                                    <li>
                                        <button
                                            onClick={handleBackToDashboard}
                                            className="hover:text-gray-700 hover:underline"
                                        >
                                            Dashboard
                                        </button>
                                    </li>
                                    <li aria-hidden="true" className="text-gray-400">›</li>
                                    <li>
                                        <button
                                            onClick={() => navigate('/team-members')}
                                            className="hover:text-gray-700 hover:underline"
                                        >
                                            Team Members
                                        </button>
                                    </li>
                                    <li aria-hidden="true" className="text-gray-400">›</li>
                                    <li className="text-gray-900 font-medium" aria-current="page">
                                        {selectedMember ? selectedMember.name : 'Timeline'}
                                    </li>
                                </ol>
                            </nav>
                            <h1 className="text-3xl font-bold text-gray-900">Team Member Timeline</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                View tasks assigned to each team member in chronological order
                            </p>
                        </div>
                        <Button variant="secondary" onClick={handleBackToDashboard}>
                            ← Back to Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content (AC11: Responsive design) */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Team Member Selector (AC9, AC12, AC13) */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                        <TeamMemberSelector
                            members={teamMembers}
                            selectedId={selectedMemberId}
                            onSelect={setSelectedMemberId}
                            taskCounts={taskCounts}
                        />
                    </div>

                    {/* Timeline View — keyed on selectedMemberId so the component remounts on member
                        change, preventing stale D3 state from the previous member being briefly visible */}
                    {selectedMemberId && (
                        <>
                            {tasksError && (
                                <Alert variant="error">
                                    <strong>Error loading tasks:</strong>
                                    <br />
                                    {tasksError instanceof Error ? tasksError.message : 'Unknown error occurred'}
                                </Alert>
                            )}

                            {!tasksError && (
                                <MemberTimeline
                                    key={selectedMemberId}
                                    tasks={memberTasks}
                                    isLoading={tasksLoading}
                                    memberId={selectedMemberId}
                                />
                            )}
                        </>
                    )}

                    {/* Empty state when no member selected */}
                    {!selectedMemberId && teamMembers.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12">
                            <div className="text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                    />
                                </svg>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">
                                    Select a Team Member
                                </h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    Choose a team member from the dropdown above to view their task timeline and
                                    workload distribution.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* No team members state */}
                    {teamMembers.length === 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12">
                            <div className="text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                    />
                                </svg>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">No Team Members</h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    You don&apos;t have any team members yet. Add team members from the dashboard to view
                                    their timelines.
                                </p>
                                <Button onClick={handleBackToDashboard} className="mt-4">
                                    Go to Dashboard
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};
